import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API, { fmt } from '@/lib/api';
import { vatLabel } from '@/lib/taxDisplay';
import { lijnPrijsVan } from '@/lib/artikelPrijs';
import { MagnifyingGlass, CashRegister, Receipt, CalendarBlank, FileArrowDown, Printer, Plus, Minus, X, Warning, Monitor, Copy, QrCode, WifiSlash } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import KlantZoeker from '@/components/KlantZoeker';
import VariantPickerModal from '@/components/VariantPickerModal';
import VerzendlabelModal from '@/components/VerzendlabelModal';
import { fmtDec, pf } from '@/lib/numFmt';
import { cacheKlanten, cacheArtikels, getCachedKlanten, getCachedArtikels, enqueueAction, getPendingCount as getOfflinePending } from '@/lib/offlineStore';
import { artikelBtw } from '@/utils/btwKlant';

const BETAALWIJZES = [
  { key: 'contant', label: 'Contant', kleur: '#008A27', bg: '#DCFCE7' },
  { key: 'bancontact', label: 'Bancontact', kleur: '#002FA7', bg: '#E6ECFA' },
  { key: 'visa', label: 'Visa / Mastercard', kleur: '#7C3AED', bg: '#EDE9FE' },
  { key: 'maestro', label: 'Maestro', kleur: '#DC2626', bg: '#FEE2E2' },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function KassaPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const magWinst = user?.role === 'admin' || !!user?.mag_winst_zien;
  // Kassa-machtigingen — admin krijgt alles, verkoper enkel wat is toegekend in zijn machtigingen-matrix
  const m = user?.machtigingen || {};
  const isAdmin = user?.role === 'admin';
  const magRetour = isAdmin || !!m.kassa_retour;
  const magKorting = isAdmin || !!m.kassa_korting;
  const magZRapport = isAdmin || !!m.kassa_z_rapport;
  const magTicketOpenen = isAdmin || !!m.kassa_ticket_openen;
  const magTicketVerwijderen = isAdmin || !!m.kassa_ticket_verwijderen;

  const [tab, setTab] = useState('kassa'); // kassa | z | maand
  const [verkopen, setVerkopen] = useState([]);
  const [tixLimit, setTixLimit] = useState(() => parseInt(localStorage.getItem('kassa-tix-limit') || '20', 10));
  const [artikels, setArtikels] = useState([]);
  const [klanten, setKlanten] = useState([]);
  const [artParams, setArtParams] = useState(null);
  const [inst, setInst] = useState({});

  // POS state
  const [lijnen, setLijnen] = useState([]);
  const [omschr, setOmschr] = useState('');
  const [prijs, setPrijs] = useState('');
  const [artSearch, setArtSearch] = useState('');
  const [prijsGroep, setPrijsGroep] = useState('basic');

  // Laad actieve prijsgroep-keuze uit instellingen zodra artParams binnenkomt
  useEffect(() => {
    if (artParams?.kassa_actieve_groep) {
      setPrijsGroep(artParams.kassa_actieve_groep);
    }
  }, [artParams?.kassa_actieve_groep]);
  const [klantId, setKlantId] = useState('');
  const [betaalModus, setBetaalModus] = useState(false);
  const [betaalBedragen, setBetaalBedragen] = useState({ contant: 0, bancontact: 0, visa: 0, maestro: 0 });
  const [actieveBw, setActieveBw] = useState('contant'); // welke betaalwijze krijgt quick-amount inputs
  const [laatsteTicket, setLaatsteTicket] = useState(null);

  // Rapport state
  const [zDatum, setZDatum] = useState(todayISO());
  const [zRapport, setZRapport] = useState(null);
  const [maand, setMaand] = useState(thisMonth());
  const [maandData, setMaandData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [variantPicker, setVariantPicker] = useState(null); // {artikel} of null
  const [retourModus, setRetourModus] = useState(false); // true → ticket wordt geboekt als retour (voorraad +1, totaal negatief, KR-prefix)
  const [tixDetail, setTixDetail] = useState(null); // open ticket-detail modal
  const [verzendOpen, setVerzendOpen] = useState(null); // {ticket} of null

  // Geselecteerde klant (voor blokkade-check)
  const geselecteerdeKlant = useMemo(() => klanten.find(k => k.id === klantId) || null, [klanten, klantId]);

  // Klant-display
  const [displayAan, setDisplayAan] = useState(() => localStorage.getItem('kassa_display_aan') === '1');
  const [displayCode, setDisplayCode] = useState('');
  const [toonDisplayPanel, setToonDisplayPanel] = useState(false);
  const pushTimer = useRef(null);

  // Offline-status voor kassa: hint dat afrekenen lokaal wordt opgeslagen
  const [isOnline, setIsOnline] = useState(() => (typeof navigator !== 'undefined' ? navigator.onLine : true));
  const [pendingOffline, setPendingOffline] = useState(0);
  useEffect(() => {
    const refresh = async () => {
      try { setPendingOffline(await getOfflinePending()); } catch { /* noop */ }
    };
    refresh();
    const on = () => { setIsOnline(true); refresh(); };
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    const poll = setInterval(refresh, 6000);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
      clearInterval(poll);
    };
  }, []);

  const load = () => {
    API.get('/kassa').then(r => setVerkopen(r.data)).catch(() => {});
    API.get('/artikels')
      .then(r => {
        const arts = r.data || [];
        setArtikels(arts);
        cacheArtikels(arts).catch(() => { /* best-effort */ });
      })
      .catch(async () => {
        const cached = await getCachedArtikels().catch(() => []);
        if ((cached || []).length > 0) setArtikels(cached);
      });
    API.get('/klanten')
      .then(r => {
        const ks = r.data || [];
        setKlanten(ks);
        cacheKlanten(ks).catch(() => { /* best-effort */ });
      })
      .catch(async () => {
        const cached = await getCachedKlanten().catch(() => []);
        if ((cached || []).length > 0) setKlanten(cached);
      });
    API.get('/artikel-params').then(r => setArtParams(r.data)).catch(() => {});
    API.get('/instellingen').then(r => setInst(r.data || {})).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (tab === 'z') API.get('/kassa/z-rapport', { params: { datum: zDatum } }).then(r => setZRapport(r.data)).catch(() => {});
  }, [tab, zDatum]);

  useEffect(() => {
    if (tab === 'maand') API.get('/kassa/maandrapport', { params: { maand } }).then(r => setMaandData(r.data)).catch(() => {});
  }, [tab, maand]);

  // Klant-display: code ophalen zodra ingeschakeld
  useEffect(() => {
    if (displayAan && !displayCode) {
      API.post('/kassa/display/init').then(r => setDisplayCode(r.data.code)).catch(() => {});
    }
  }, [displayAan, displayCode]);

  // Klant-display: push state bij elke winkelwagen-wijziging (debounced)
  useEffect(() => {
    if (!displayAan || !displayCode) return;
    if (pushTimer.current) clearTimeout(pushTimer.current);
    pushTimer.current = setTimeout(() => {
      const status = laatsteTicket ? 'done' : (betaalModus ? 'paying' : (lijnen.length ? 'shopping' : 'idle'));
      const tot = laatsteTicket ? laatsteTicket.totaal : lijnen.reduce((s, l) => s + l.prijs * (l.aantal || 1), 0);
      API.post('/kassa/display/push', {
        status,
        lijnen: laatsteTicket?.lijnen || lijnen,
        totaal: tot,
        gedekt: betaalModus ? Object.values(betaalBedragen).reduce((s, v) => s + (parseFloat(v) || 0), 0) : 0,
        wisselgeld: laatsteTicket?.wisselgeld || 0,
        ticket_id: laatsteTicket?.id || '',
      }).catch(() => {});
    }, 250);
    return () => pushTimer.current && clearTimeout(pushTimer.current);
  }, [lijnen, betaalModus, betaalBedragen, laatsteTicket, displayAan, displayCode]);

  // Auto-reset "done" state op display na 8s
  useEffect(() => {
    if (laatsteTicket && displayAan && displayCode) {
      const t = setTimeout(() => setLaatsteTicket(null), 8000);
      return () => clearTimeout(t);
    }
  }, [laatsteTicket, displayAan, displayCode]);

  const displayURL = displayCode ? `${window.location.origin}/klant-display/${displayCode}` : '';
  const toggleDisplay = () => {
    const nw = !displayAan;
    setDisplayAan(nw);
    localStorage.setItem('kassa_display_aan', nw ? '1' : '0');
    if (nw) setToonDisplayPanel(true);
  };

  const groepen = artParams?.groepen || [];

  const totaal = useMemo(() => lijnen.reduce((s, l) => s + l.prijs * (l.aantal || 1), 0), [lijnen]);
  const gedekt = Object.values(betaalBedragen).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const wisselgeld = Math.max(0, gedekt - totaal);
  const tekort = Math.max(0, totaal - gedekt);

  const addArtikel = (art, variant = null) => {
    // Heeft varianten? Open de picker tenzij we al een variant gekregen hebben
    if (!variant && art.varianten_actief && (art.varianten || []).length > 0) {
      setVariantPicker({ artikel: art });
      return;
    }
    const isPack = art.eenheid === 'pack' && art.pack_grootte > 0;
    let unit = (art.prijzen || {})[prijsGroep] || 0;
    if (variant && variant.prijs_override !== null && variant.prijs_override !== undefined) {
      unit = variant.prijs_override;
    }
    const lp = lijnPrijsVan(art, unit);
    const variantSuffix = variant
      ? ' · ' + Object.entries(variant.combo || {}).map(([, v]) => v).join(' / ')
      : '';
    const baseLabel = isPack ? `${art.omschrijving || art.art_code} (${art.pack_grootte} st.)` : (art.omschrijving || art.art_code);
    const label = baseLabel + variantSuffix;
    setLijnen(l => {
      const idx = l.findIndex(x => x.art_id === art.id && (x.variant_combo_key || null) === (variant ? variant.combo_key : null));
      if (idx >= 0) {
        const cp = [...l]; cp[idx] = { ...cp[idx], aantal: (cp[idx].aantal || 1) + 1 }; return cp;
      }
      return [...l, {
        omschr: label,
        prijs: lp,
        aantal: 1,
        art_id: art.id,
        btw: artikelBtw(art),
        foto_url: (variant?.foto_url || art.foto_url || ''),
        variant_combo_key: variant?.combo_key,
        variant_combo: variant?.combo,
        variant_art_code: variant?.art_code,
      }];
    });
    setArtSearch('');
    setVariantPicker(null);
  };

  const kAdd = () => {
    const p = pf(prijs);
    if (!p) return;
    setLijnen(l => [...l, { omschr: omschr || 'Artikel', prijs: p, aantal: 1, btw: 21 }]);
    setOmschr(''); setPrijs('');
  };

  const rmLijn = (i) => setLijnen(l => l.filter((_, j) => j !== i));
  const updAantal = (i, val) => setLijnen(l => l.map((x, j) => j === i ? { ...x, aantal: Math.max(1, val) } : x));

  const openBetaal = () => {
    if (!lijnen.length) { alert('Voeg eerst artikels toe.'); return; }
    setBetaalBedragen({ contant: 0, bancontact: 0, visa: 0, maestro: 0 });
    setActieveBw('contant');
    setBetaalModus(true);
  };

  const snelBetaal = (key) => {
    // Sneltoets: volledig bedrag via 1 betaalwijze
    setBetaalBedragen({ contant: 0, bancontact: 0, visa: 0, maestro: 0, [key]: totaal.toFixed(2) });
    setActieveBw(key);
  };

  const setBwBedrag = (key, val) => {
    setBetaalBedragen(s => ({ ...s, [key]: val }));
  };

  const quickAmount = (bedrag) => {
    // Voeg toe aan de actieve betaalwijze
    const huidig = pf(betaalBedragen[actieveBw]);
    setBwBedrag(actieveBw, (huidig + bedrag).toFixed(2));
  };

  const quickExact = () => {
    // Vul het nog niet gedekte bedrag in op actieve bw
    const andere = Object.entries(betaalBedragen).filter(([k]) => k !== actieveBw)
      .reduce((s, [, v]) => s + (parseFloat(v) || 0), 0);
    const resterend = Math.max(0, totaal - andere);
    setBwBedrag(actieveBw, resterend.toFixed(2));
  };

  const afrekenen = async () => {
    if (geselecteerdeKlant?.geblokkeerd) {
      alert('🚫 Klant is geblokkeerd — verkoop niet toegestaan.');
      return;
    }
    if (tekort > 0.01) { alert(`Er ontbreekt nog € ${tekort.toFixed(2)}`); return; }
    const bws = BETAALWIJZES
      .map(b => ({ type: b.key, bedrag: pf(betaalBedragen[b.key]) }))
      .filter(b => b.bedrag > 0);
    // Cash: boek het correcte bedrag (niet de overshoot), wisselgeld is ter info
    const cashIdx = bws.findIndex(b => b.type === 'contant');
    if (cashIdx >= 0 && wisselgeld > 0) bws[cashIdx].bedrag -= wisselgeld;
    // Bij retour: aantallen, totaal en betaalbedragen worden NEGATIEF (= terugbetaling)
    const sign = retourModus ? -1 : 1;
    const retourLijnen = retourModus
      ? lijnen.map(l => ({ ...l, aantal: -Math.abs(parseFloat(l.aantal) || 0) }))
      : lijnen;
    const retourBws = retourModus ? bws.map(b => ({ ...b, bedrag: -Math.abs(b.bedrag) })) : bws;
    const payload = {
      totaal: sign * Math.abs(totaal),
      lijnen: retourLijnen,
      betaalwijzes: retourBws,
      klant_id: klantId || null,
      betaalwijze: bws[0]?.type || 'contant', // backward compat
      is_retour: retourModus,
    };
    setBusy(true);

    // OFFLINE: queue in IndexedDB, toon offline-bevestiging, sync gebeurt automatisch later
    if (!navigator.onLine) {
      try {
        const localId = await enqueueAction({
          method: 'POST',
          url: '/kassa',
          body: payload,
          label: `Kassa-ticket €${Math.abs(totaal).toFixed(2)}${retourModus ? ' (retour)' : ''}`,
          kind: 'kassa_ticket',
          klantId: klantId || null,
        });
        const localTicket = {
          id: `OFFLINE-${String(localId).slice(-5)}`,
          ticketnummer: `OFFLINE-${String(localId).slice(-5)}`,
          totaal: payload.totaal,
          lijnen: payload.lijnen,
          betaalwijzes: payload.betaalwijzes,
          datum: new Date().toISOString(),
          offline: true,
          wisselgeld,
        };
        setLaatsteTicket(localTicket);
        setLijnen([]); setKlantId(''); setBetaalModus(false); setRetourModus(false);
        try { setPendingOffline(await getOfflinePending()); } catch { /* noop */ }
        try { navigator.vibrate?.(60); } catch { /* niet kritiek */ }
      } catch (e) {
        alert('Kon ticket niet lokaal opslaan: ' + (e.message || e));
      } finally {
        setBusy(false);
      }
      return;
    }

    try {
      const { data } = await API.post('/kassa', payload);
      setLaatsteTicket({ ...data, wisselgeld });
      setLijnen([]); setKlantId(''); setBetaalModus(false); setRetourModus(false);
      load();
    } catch (e) {
      // Netwerk-fout (geen response) → queue offline
      if (!e.response) {
        try {
          const localId = await enqueueAction({
            method: 'POST',
            url: '/kassa',
            body: payload,
            label: `Kassa-ticket €${Math.abs(totaal).toFixed(2)}${retourModus ? ' (retour)' : ''}`,
            kind: 'kassa_ticket',
            klantId: klantId || null,
          });
          const localTicket = {
            id: `OFFLINE-${String(localId).slice(-5)}`,
            ticketnummer: `OFFLINE-${String(localId).slice(-5)}`,
            totaal: payload.totaal,
            lijnen: payload.lijnen,
            betaalwijzes: payload.betaalwijzes,
            datum: new Date().toISOString(),
            offline: true,
            wisselgeld,
          };
          setLaatsteTicket(localTicket);
          setLijnen([]); setKlantId(''); setBetaalModus(false); setRetourModus(false);
          try { setPendingOffline(await getOfflinePending()); } catch { /* noop */ }
        } catch (qe) {
          alert('Netwerk-fout EN lokaal opslaan mislukt: ' + (qe.message || qe));
        }
      } else {
        alert('Fout bij afrekenen: ' + (e.response?.data?.detail || e.message));
      }
    }
    setBusy(false);
  };

  const printBon = (ticket) => {
    const w = window.open('', '_blank', 'width=400,height=720');
    const now = new Date(ticket._created || Date.now());
    const esc = (s) => (s || '').replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));

    // Placeholder-substitutie voor header/voetekst/voorwaarden
    const ph = {
      '{bedrijf}': inst.naam || '',
      '{btw}': inst.btw || '',
      '{adres}': inst.adres || '',
      '{tel}': inst.tel || '',
      '{email}': inst.email || '',
      '{iban}': inst.iban || '',
      '{datum}': now.toLocaleDateString('nl-BE'),
      '{tijd}': now.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' }),
      '{ticket}': ticket.id || '',
    };
    const fill = (t) => {
      let out = t || '';
      Object.entries(ph).forEach(([k, v]) => { out = out.split(k).join(v); });
      return out;
    };

    const toonBedrijfsinfo = inst.kassa_ticket_toon_bedrijfsinfo !== false;
    const toonBtw = inst.kassa_ticket_toon_btw !== false;
    const toonLogo = inst.kassa_ticket_toon_logo !== false;
    const toonFotos = !!inst.kassa_ticket_toon_fotos;
    const logoUrl = inst.kassa_ticket_logo || inst.logo || '';
    const LOGO_BREEDTES = { small: 80, medium: 140, large: 200 };
    const logoPx = LOGO_BREEDTES[inst.kassa_ticket_logo_breedte] || 140;

    // Bouw een art_id → foto_url map op basis van de geladen artikels-lijst
    const fotoMap = {};
    if (toonFotos) {
      (artikels || []).forEach(a => {
        if (a?.foto_url) fotoMap[a.id] = `${process.env.REACT_APP_BACKEND_URL}${a.foto_url}`;
      });
    }

    const rows = (ticket.lijnen || []).map(l => {
      const foto = toonFotos ? (fotoMap[l.art_id] || '') : '';
      const fotoCell = toonFotos
        ? `<td style="width:34px;padding:2px 4px 2px 0;vertical-align:middle">${
            foto ? `<img src="${foto}" style="width:30px;height:30px;object-fit:cover;border:1px solid #ccc;border-radius:2px;display:block" />` : ''
          }</td>`
        : '';
      return `<tr>${fotoCell}<td>${(l.aantal || 1)}× ${esc(l.omschr)}</td><td style="text-align:right;vertical-align:middle">€ ${((l.prijs || 0) * (l.aantal || 1)).toFixed(2)}</td></tr>`;
    }).join('');
    const bws = (ticket.betaalwijzes || []).map(b =>
      `<tr><td>${esc(b.type)}</td><td style="text-align:right">€ ${(b.bedrag || 0).toFixed(2)}</td></tr>`
    ).join('');

    // BTW-opsplitsing
    const btwMap = {};
    (ticket.lijnen || []).forEach(l => {
      const pct = Number.isFinite(parseFloat(l.btw)) ? parseFloat(l.btw) : 21;
      const excl = (l.prijs || 0) * (l.aantal || 1);
      if (!btwMap[pct]) btwMap[pct] = { excl: 0, btw: 0 };
      btwMap[pct].excl += excl;
      btwMap[pct].btw += excl * (pct / 100);
    });
    const btwRows = Object.entries(btwMap).sort().map(([pct, v]) =>
      `<tr><td>${vatLabel(pct)}</td><td style="text-align:right">€ ${v.excl.toFixed(2)}</td><td style="text-align:right">€ ${v.btw.toFixed(2)}</td><td style="text-align:right">€ ${(v.excl + v.btw).toFixed(2)}</td></tr>`
    ).join('');

    const headerInfo = toonBedrijfsinfo ? `
      <div class="center">
        <div class="big">${esc(inst.naam || 'Kassa')}</div>
        ${inst.adres ? `<div>${esc(inst.adres)}</div>` : ''}
        ${inst.btw ? `<div>BTW ${esc(inst.btw)}</div>` : ''}
        ${(inst.tel || inst.email) ? `<div>${esc(inst.tel || '')}${inst.tel && inst.email ? ' · ' : ''}${esc(inst.email || '')}</div>` : ''}
      </div>` : '';

    const logoBlock = (toonLogo && logoUrl) ? `
      <div class="center" style="margin-bottom:6px">
        <img src="${logoUrl}" alt="logo" style="max-width:${logoPx}px;max-height:80px;object-fit:contain;display:inline-block" />
      </div>` : '';

    const customHeader = inst.kassa_ticket_header ? `<div class="center" style="margin-top:6px;white-space:pre-wrap">${esc(fill(inst.kassa_ticket_header))}</div>` : '';
    const customFooter = inst.kassa_ticket_voetekst ? `<div class="hr"></div><div class="center" style="white-space:pre-wrap">${esc(fill(inst.kassa_ticket_voetekst))}</div>` : '';
    const voorwaarden = inst.kassa_ticket_voorwaarden ? `
      <div class="hr"></div>
      <div class="voorwaarden">
        <div class="vw-title">Verkoopvoorwaarden</div>
        ${esc(fill(inst.kassa_ticket_voorwaarden))}
      </div>` : '';

    w.document.write(`<html><head><title>Ticket ${esc(ticket.id)}</title>
      <style>
        @page { size: 80mm auto; margin: 4mm; }
        body{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11px;padding:8px;color:#000;margin:0}
        .big{font-size:14px;font-weight:bold}
        table{width:100%;border-collapse:collapse}
        td{padding:2px 0;vertical-align:top}
        .hr{border-top:1px dashed #666;margin:6px 0}
        .center{text-align:center}
        .right{text-align:right}
        .voorwaarden{font-size:9px;color:#333;line-height:1.4;text-align:justify;white-space:pre-wrap}
        .vw-title{font-weight:bold;text-transform:uppercase;margin-bottom:4px;color:#000;text-align:center}
      </style></head><body>
      ${logoBlock}
      ${headerInfo}
      ${customHeader}
      <div class="hr"></div>
      ${ticket.is_retour ? `<div class="center" style="background:#fee2e2;color:#991b1b;font-weight:bold;padding:4px;border:2px dashed #991b1b;font-size:13px">↩️ RETOUR / TERUGBETALING</div><div class="hr"></div>` : ''}
      <div class="center">Ticket <b>${esc(ticket.id)}</b><br/>${now.toLocaleDateString('nl-BE')} · ${now.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}</div>
      <div class="hr"></div>
      <table>${rows}</table>
      <div class="hr"></div>
      <table>
        <tr><td class="big">TOTAAL</td><td class="right big">€ ${(ticket.totaal || 0).toFixed(2)}</td></tr>
      </table>
      ${toonBtw && btwRows ? `<div class="hr"></div>
        <table style="font-size:10px">
          <tr style="color:#666"><td>BTW</td><td class="right">Excl.</td><td class="right">BTW</td><td class="right">Incl.</td></tr>
          ${btwRows}
        </table>` : ''}
      <div class="hr"></div>
      <table>${bws}</table>
      ${(ticket.wisselgeld || 0) > 0 ? `<div style="margin-top:4px">Wisselgeld: € ${ticket.wisselgeld.toFixed(2)}</div>` : ''}
      ${customFooter}
      ${voorwaarden}
      <script>window.onload=function(){window.print();setTimeout(function(){window.close()},500)};</script>
      </body></html>`);
    w.document.close();
  };

  const dagNaarFactuur = async () => {
    if (!window.confirm(`Alle kassa-tickets van ${zDatum} bundelen tot één factuur op "Kassaverkoop"?`)) return;
    setBusy(true);
    try {
      const { data } = await API.post('/kassa/dag-naar-factuur', { datum: zDatum });
      alert(`Factuur ${data.id} aangemaakt voor € ${data.totaal.toFixed(2)}`);
      API.get('/kassa/z-rapport', { params: { datum: zDatum } }).then(r => setZRapport(r.data));
      load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
    setBusy(false);
  };

  const filteredArt = artikels.filter(a =>
    artSearch && (a.omschrijving?.toLowerCase().includes(artSearch.toLowerCase()) ||
                  a.art_code?.toLowerCase().includes(artSearch.toLowerCase()) ||
                  (a.ean || '').includes(artSearch))
  )
    // Sorteer op categorie → art_code, consistent met Artikels & VerkoopPage.
    .sort((a, b) => {
      const catA = a.categorie || 'zzz';
      const catB = b.categorie || 'zzz';
      const c = catA.localeCompare(catB, 'nl-BE');
      if (c !== 0) return c;
      return (a.art_code || '').localeCompare(b.art_code || '', 'nl-BE');
    });

  return (
    <div data-testid="kassa-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="page-heading">Kassa</h1>
        <div className="flex items-center gap-3">
          {(!isOnline || pendingOffline > 0) && (
            <span
              data-testid="kassa-offline-badge"
              className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold ${isOnline ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}
              title={isOnline ? `${pendingOffline} tickets wachten op sync` : 'Geen netwerk — tickets worden lokaal opgeslagen'}
            >
              <WifiSlash size={12} weight="bold" />
              {isOnline ? `Sync ${pendingOffline}` : `Offline${pendingOffline ? ` · ${pendingOffline}` : ''}`}
            </span>
          )}
          <button
            onClick={toggleDisplay}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded border transition ${displayAan ? 'bg-green-50 border-green-300 text-green-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            data-testid="kassa-display-toggle"
            title={displayAan ? 'Klant-display actief — klik om paring-info te tonen/verbergen' : 'Schakel klant-display in'}
          >
            <Monitor size={14} />
            {displayAan ? `Display AAN${displayCode ? ' · ' + displayCode : ''}` : 'Klant-display'}
          </button>
          {displayAan && (
            <button onClick={() => setToonDisplayPanel(v => !v)} className="text-xs text-[#002FA7] hover:underline" data-testid="kassa-display-info">
              {toonDisplayPanel ? 'Verberg' : 'Info'}
            </button>
          )}
          <div className="flex gap-1 bg-gray-100 p-1 rounded">
            <TabBtn actief={tab === 'kassa'} onClick={() => setTab('kassa')} icon={<CashRegister size={14} />} label="Kassa" testid="tab-kassa" />
            {magZRapport && <TabBtn actief={tab === 'z'} onClick={() => setTab('z')} icon={<Receipt size={14} />} label="Z-rapport" testid="tab-z" />}
            {magZRapport && <TabBtn actief={tab === 'maand'} onClick={() => setTab('maand')} icon={<CalendarBlank size={14} />} label="Maand" testid="tab-maand" />}
          </div>
        </div>
      </div>

      {displayAan && toonDisplayPanel && displayCode && (
        <div className="mb-4 p-4 rounded border border-green-200" style={{ background: 'linear-gradient(90deg,#F0FDF4 0%,#ECFDF5 100%)' }}>
          <div className="flex items-start gap-4">
            <div className="p-3 rounded bg-white border border-green-200">
              <Monitor size={32} className="text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider text-green-700 font-bold mb-1">Klant-display actief</div>
              <div className="text-sm text-gray-800 mb-2">Open onderstaande link op een 2e scherm/tablet/klant-monitor. De totalen, artikels en betaal-QR worden live meegestuurd.</div>
              <div className="flex items-center gap-2 mb-2">
                <code className="text-[11px] font-mono px-2 py-1 bg-white border border-gray-200 rounded break-all flex-1" data-testid="kassa-display-url">{displayURL}</code>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => { navigator.clipboard?.writeText(displayURL); }}
                  data-testid="kassa-display-copy"
                ><Copy size={12} /> Kopieer</button>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => window.open(displayURL, '_blank', 'noopener')}
                  data-testid="kassa-display-open"
                >Open in nieuw venster</button>
              </div>
              <div className="text-[11px] text-gray-500">Pairing-code: <span className="font-mono font-bold text-gray-800">{displayCode}</span> · Bewaar deze code om hetzelfde display later terug te verbinden.</div>
            </div>
          </div>
        </div>
      )}

      {tab === 'kassa' && (
        <>
          <div className="mb-3 flex items-start gap-2">
            <div className="flex-1">
              <KlantZoeker klanten={klanten} klantId={klantId} onChange={setKlantId} />
              {klantId === '' && <div className="text-xs text-gray-500 mt-1">Geen klant gekozen → ticket wordt geboekt op systeem-klant <b>Kassaverkoop</b>.</div>}
            </div>
            {magRetour && (
              <button
                type="button"
                onClick={() => {
                  if (lijnen.length > 0 && !window.confirm(retourModus
                    ? 'Retour-modus uitschakelen? De huidige lijnen blijven staan, maar worden weer als verkoop geboekt.'
                    : 'Retour-modus inschakelen? De huidige lijnen worden bij afrekenen als terugbetaling geboekt (voorraad +x, totaal negatief).')) return;
                  setRetourModus(v => !v);
                }}
                className={`px-3 py-2 rounded border-2 text-xs font-bold inline-flex items-center gap-1.5 shrink-0 transition ${
                  retourModus
                    ? 'bg-red-600 border-red-700 text-white shadow-sm'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-red-400 hover:text-red-600'
                }`}
                data-testid="kassa-retour-toggle"
                title={retourModus
                  ? 'RETOUR-MODUS actief — klik om uit te schakelen'
                  : 'Schakel retour-modus in om een terugname te boeken (voorraad gaat terug omhoog, totaal wordt negatief)'}
              >
                ↩️ {retourModus ? 'Retour AAN' : 'Retour-modus'}
              </button>
            )}
          </div>

          {retourModus && (
            <div className="mb-3 p-3 rounded border-2 border-red-300 bg-red-50 flex items-center gap-3" data-testid="kassa-retour-banner">
              <span className="text-2xl">↩️</span>
              <div className="flex-1">
                <div className="text-sm font-bold text-red-900">RETOUR-MODUS — bedragen worden teruggeboekt</div>
                <div className="text-[11px] text-red-700 mt-0.5">
                  Voeg de artikelen toe die de klant terugbrengt (positieve aantallen). Bij afrekenen worden voorraad <b>+x</b> bijgeboekt en totaal/betaalwijze als <b>negatief</b> bedrag gelogd. Ticket-nummer krijgt prefix <b>KR-</b>.
                </div>
              </div>
              <button
                onClick={() => setRetourModus(false)}
                className="text-red-700 hover:bg-red-100 rounded p-1.5"
                title="Annuleer retour-modus"
                data-testid="kassa-retour-banner-close"
              ><X size={16} /></button>
            </div>
          )}

          {/* Rode banner: gekozen klant is geblokkeerd → afrekenen niet toegestaan */}
          {geselecteerdeKlant?.geblokkeerd && (
            <div
              className="mb-3 px-4 py-3 border-2 border-red-500 bg-red-50 text-red-900 rounded-lg flex items-start gap-3"
              data-testid="kassa-klant-geblokkeerd-banner"
            >
              <span className="text-2xl shrink-0 leading-none">🚫</span>
              <div className="flex-1">
                <div className="font-bold text-base">Klant is geblokkeerd</div>
                <div className="text-xs mt-0.5">
                  <strong>{geselecteerdeKlant.bedrijf || `${geselecteerdeKlant.voorn || ''} ${geselecteerdeKlant.achtern || ''}`.trim() || geselecteerdeKlant.naam}</strong> staat op blokkade — afrekenen op deze klant is niet toegestaan.
                  {geselecteerdeKlant.geblokkeerd_reden && (
                    <span className="block mt-1 italic">Reden: {geselecteerdeKlant.geblokkeerd_reden}</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="text-xs font-bold text-red-800 hover:text-red-900 underline shrink-0 self-center"
                onClick={() => setKlantId('')}
                data-testid="kassa-deselecteer-geblokkeerd"
              >
                Klant deselecteren
              </button>
            </div>
          )}

          {/* Afgerekend overlay */}
          {laatsteTicket && (
            <div className="mb-3 flex items-center justify-between p-4 rounded border-2 border-green-400 bg-green-50" data-testid="kassa-success-banner">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center text-xl font-bold">✓</div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-green-700 font-bold">Afgerekend — ticket {laatsteTicket.id}</div>
                  <div className="text-2xl font-bold text-green-800 font-mono">{fmt(laatsteTicket.totaal)}</div>
                  {laatsteTicket.wisselgeld > 0 && <div className="text-sm text-green-700">Wisselgeld uit kassa: <b>{fmt(laatsteTicket.wisselgeld)}</b></div>}
                </div>
              </div>
              <div className="flex gap-2">
                <button className="px-4 py-3 rounded bg-white border-2 border-green-300 text-green-700 font-semibold hover:bg-green-100 flex items-center gap-2" onClick={() => printBon(laatsteTicket)} data-testid="kassa-print-last"><Printer size={18} /> Bon</button>
                <button className="px-4 py-3 rounded bg-green-600 text-white font-semibold hover:bg-green-700" onClick={() => setLaatsteTicket(null)} data-testid="kassa-success-dismiss">Sluiten</button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
            {/* LINKS: Artikel-grid (3/5) */}
            <div className="lg:col-span-3 rounded" style={{ background: '#fff', border: '1px solid #E5E5E5' }}>
              <div className="p-3 border-b border-gray-200" style={{ background: '#F0F4FF' }}>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="text-sm font-bold uppercase tracking-wider text-[#002FA7]">Artikels</span>
                  {groepen.length > 0 && (() => {
                    const huidige = groepen.find(g => g.key === prijsGroep) || groepen[0];
                    return (
                      <div className="ml-auto inline-flex items-center gap-2" data-testid="kassa-prijsgroep-indicator">
                        <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Prijsgroep:</span>
                        <span
                          className="px-2.5 py-1 text-xs font-bold text-white"
                          style={{ background: huidige?.color || '#002FA7' }}
                          data-testid={`kassa-prijsgroep-${huidige?.key || 'basic'}`}
                        >
                          {huidige?.label || 'Basic'}
                        </span>
                        <a href="/instellingen?tab=kassa_prijsgroepen" className="text-[10px] text-gray-400 hover:text-[#002FA7] underline" title="Wijzigen via Instellingen">wijzigen</a>
                        {/* Hidden legacy dropdown voor backward-compat tests */}
                        <select className="hidden" value={prijsGroep} onChange={e => setPrijsGroep(e.target.value)} data-testid="kassa-prijsgroep">
                          {groepen.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
                        </select>
                      </div>
                    );
                  })()}
                </div>
                <div className="relative">
                  <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    className="w-full text-base rounded border border-gray-300 focus:border-[#002FA7] focus:outline-none"
                    style={{ padding: '10px 10px 10px 38px', minHeight: 44 }}
                    placeholder="Zoek artikel, code of scan EAN..."
                    value={artSearch}
                    onChange={e => setArtSearch(e.target.value)}
                    data-testid="kassa-art-search"
                  />
                </div>
              </div>
              <div className="p-3" style={{ maxHeight: 'calc(100vh - 310px)', overflowY: 'auto', minHeight: 320 }}>
                {artikels.length === 0 && <div className="text-center text-gray-400 py-10 text-sm">Geen artikels</div>}
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2">
                  {(artSearch ? filteredArt : artikels).map(a => {
                    const isPack = a.eenheid === 'pack' && a.pack_grootte > 0;
                    const lp = lijnPrijsVan(a, a.prijzen?.[prijsGroep] || 0);
                    return (
                      <button
                        key={a.id}
                        onClick={() => addArtikel(a)}
                        className="group text-left p-2 rounded border border-gray-200 hover:border-[#002FA7] active:scale-[0.97] transition bg-white hover:bg-[#F0F4FF] flex flex-col"
                        style={{ minHeight: 92 }}
                        data-testid={`kassa-art-${a.id}`}
                      >
                        <div className="flex-1 min-w-0 pr-1">
                          <div className="text-xs sm:text-sm font-semibold leading-tight line-clamp-2 text-gray-800">{a.omschrijving || a.art_code}</div>
                          <div className="text-[10px] text-gray-400 font-mono mt-0.5">{a.art_code}{isPack && ` (${a.pack_grootte} st.)`}</div>
                        </div>
                        {/* Onderste rij: prijs links, mini-foto rechts */}
                        <div className="flex items-end justify-between gap-2 mt-1.5">
                          <div className="text-base sm:text-lg font-bold text-[#002FA7]">{fmt(lp)}</div>
                          <div className="w-14 h-14 bg-gray-50 border border-gray-200 rounded overflow-hidden flex items-center justify-center shrink-0">
                            {a.foto_url ? (
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${a.foto_url}`}
                                alt={a.omschrijving || a.art_code}
                                loading="lazy"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-xl text-gray-300">🖼</span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {artSearch && filteredArt.length === 0 && <div className="text-center text-gray-400 py-10 text-sm">Geen resultaten voor &quot;{artSearch}&quot;</div>}
              </div>
            </div>

            {/* RECHTS: Winkelwagen / Betaling (2/5) */}
            <div className="lg:col-span-2 rounded flex flex-col" style={{ background: '#fff', border: '1px solid #E5E5E5', minHeight: 520 }}>
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between" style={{ background: betaalModus ? '#F0F4FF' : '#F3F4F6' }}>
                <span className="text-sm font-bold uppercase tracking-wider" style={{ color: betaalModus ? '#002FA7' : '#6B7280' }}>{betaalModus ? '💳 Betaling' : '🛒 Winkelwagen'}</span>
                <span className="text-xs text-gray-500">{lijnen.length} {lijnen.length === 1 ? 'regel' : 'regels'}</span>
              </div>

              {!betaalModus && (
                <div className="flex-1 flex flex-col p-3">
                  {/* Totaal display */}
                  <div className="text-right mb-2">
                    <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Totaal</div>
                    <div className="text-4xl font-bold text-[#002FA7] font-mono leading-none" data-testid="kassa-display">{fmt(totaal)}</div>
                  </div>

                  {/* Lijnen */}
                  <div className="flex-1 mb-2 border border-gray-200 rounded overflow-hidden" style={{ minHeight: 180 }}>
                    <div className="h-full overflow-y-auto" style={{ maxHeight: 'calc(100vh - 500px)', minHeight: 180 }}>
                      {lijnen.length === 0 && <div className="text-center text-gray-400 py-10 text-sm px-4">Tik op een artikel of voeg een vrije regel toe</div>}
                      {lijnen.map((l, i) => (
                        <div key={i} className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-gray-100">
                          {l.foto_url && (
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${l.foto_url}`}
                              alt=""
                              className="w-10 h-10 object-cover rounded border border-gray-200 shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate" title={l.omschr}>{l.omschr}</div>
                            <div className="text-[11px] text-gray-500">{fmt(l.prijs)} / stuk</div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              className="w-10 h-10 rounded bg-gray-100 hover:bg-gray-200 active:scale-95 flex items-center justify-center"
                              onClick={() => updAantal(i, (l.aantal || 1) - 1)}
                              data-testid={`kassa-min-${i}`}
                            ><Minus size={16} /></button>
                            <span className="font-bold w-8 text-center text-base">{l.aantal || 1}</span>
                            <button
                              className="w-10 h-10 rounded bg-gray-100 hover:bg-gray-200 active:scale-95 flex items-center justify-center"
                              onClick={() => updAantal(i, (l.aantal || 1) + 1)}
                              data-testid={`kassa-plus-${i}`}
                            ><Plus size={16} /></button>
                          </div>
                          <div className="text-base font-bold text-[#002FA7] font-mono w-20 text-right shrink-0">{fmt(l.prijs * (l.aantal || 1))}</div>
                          <button
                            className="w-9 h-9 rounded text-red-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center shrink-0"
                            onClick={() => rmLijn(i)}
                            data-testid={`kassa-rm-${i}`}
                            title="Verwijder regel"
                          ><X size={18} /></button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Vrije regel input */}
                  <details className="mb-2">
                    <summary className="text-xs text-gray-500 cursor-pointer select-none py-1">+ Vrije omschrijving toevoegen</summary>
                    <div className="flex gap-2 mt-2">
                      <input
                        className="flex-[2] text-sm rounded border border-gray-300 focus:border-[#002FA7] focus:outline-none"
                        style={{ padding: '10px', minHeight: 40 }}
                        placeholder="Omschrijving"
                        value={omschr}
                        onChange={e => setOmschr(e.target.value)}
                        data-testid="kassa-omschr"
                      />
                      <input
                        type="text"
                        inputMode="decimal"
                        className="flex-1 text-sm rounded border border-gray-300 focus:border-[#002FA7] focus:outline-none font-mono text-right"
                        style={{ padding: '10px', minHeight: 40 }}
                        placeholder="Prijs"
                        value={fmtDec(prijs)}
                        onChange={e => setPrijs(e.target.value.replace(',', '.'))}
                        data-testid="kassa-prijs"
                      />
                      <button
                        className="rounded bg-[#002FA7] text-white font-semibold hover:bg-[#001A5C] active:scale-95"
                        style={{ minWidth: 48, minHeight: 40 }}
                        onClick={kAdd}
                        data-testid="kassa-add-btn"
                      ><Plus size={18} /></button>
                    </div>
                  </details>

                  {/* Actieknoppen */}
                  <div className="flex gap-2 mt-auto">
                    <button
                      className="flex-1 rounded border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 active:scale-[0.98]"
                      style={{ minHeight: 56 }}
                      onClick={() => setLijnen([])}
                      disabled={!lijnen.length}
                      data-testid="kassa-clear"
                    >Wissen</button>
                    <button
                      className={`flex-[2] rounded text-white font-bold text-lg active:scale-[0.98] disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                        retourModus ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                      }`}
                      style={{ minHeight: 56 }}
                      onClick={openBetaal}
                      disabled={!lijnen.length || geselecteerdeKlant?.geblokkeerd}
                      title={geselecteerdeKlant?.geblokkeerd ? 'Klant is geblokkeerd — afrekenen niet toegestaan' : undefined}
                      data-testid="kassa-naar-betaal"
                    >{retourModus ? '↩️ Verwerk retour →' : 'Naar betaling →'}</button>
                  </div>
                </div>
              )}

              {betaalModus && (
                <div className="flex-1 flex flex-col p-3">
                  {/* Te betalen - prominent */}
                  <div className="text-center mb-3 p-4 rounded" style={{ background: '#F0F4FF', border: '2px solid #002FA7' }}>
                    <div className="text-xs uppercase tracking-wider text-gray-600 font-bold">Te betalen</div>
                    <div className="text-5xl font-bold text-[#002FA7] font-mono">{fmt(totaal)}</div>
                  </div>

                  {/* Betaalwijze tegels */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {BETAALWIJZES.map(b => {
                      const isActief = actieveBw === b.key;
                      const bedrag = pf(betaalBedragen[b.key]);
                      return (
                        <button
                          key={b.key}
                          onClick={() => setActieveBw(b.key)}
                          className="relative p-3 rounded border-2 text-left transition active:scale-[0.97]"
                          style={{
                            borderColor: isActief ? b.kleur : '#E5E5E5',
                            background: bedrag > 0 ? b.bg : '#fff',
                            minHeight: 72,
                          }}
                          data-testid={`kassa-bw-tile-${b.key}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-bold uppercase" style={{ color: b.kleur }}>{b.label}</span>
                            {isActief && <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: b.kleur, color: '#fff' }}>ACTIEF</span>}
                          </div>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={fmtDec(betaalBedragen[b.key] || '')}
                            placeholder="0,00"
                            onChange={e => setBwBedrag(b.key, e.target.value.replace(',', '.'))}
                            onFocus={() => setActieveBw(b.key)}
                            className="w-full text-2xl font-bold text-right bg-transparent font-mono focus:outline-none"
                            style={{ color: b.kleur }}
                            data-testid={`kassa-bw-${b.key}`}
                          />
                        </button>
                      );
                    })}
                  </div>

                  {/* Snel-bedrag-knoppen */}
                  <div className="mb-3">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1.5 flex items-center justify-between">
                      <span>Snel invoeren op <b style={{ color: BETAALWIJZES.find(b => b.key === actieveBw)?.kleur }}>{BETAALWIJZES.find(b => b.key === actieveBw)?.label}</b></span>
                      <button className="text-[10px] text-gray-500 hover:text-gray-800 underline" onClick={() => setBwBedrag(actieveBw, 0)} data-testid="kassa-quick-wis">Wis bedrag</button>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5">
                      {[5, 10, 20, 50, 100, 200].map(v => (
                        <button
                          key={v}
                          onClick={() => quickAmount(v)}
                          className="rounded border border-gray-300 bg-white hover:bg-gray-100 active:scale-95 font-bold text-sm"
                          style={{ minHeight: 44 }}
                          data-testid={`kassa-quick-${v}`}
                        >+€{v}</button>
                      ))}
                      <button
                        onClick={quickExact}
                        className="col-span-2 rounded bg-[#002FA7] text-white font-bold hover:bg-[#001A5C] active:scale-95"
                        style={{ minHeight: 44 }}
                        data-testid="kassa-quick-exact"
                      >Exact resterend</button>
                    </div>
                  </div>

                  {/* Gedekt / tekort / wisselgeld */}
                  <div className="p-3 rounded mb-3" style={{ background: tekort > 0.01 ? '#FEF3C7' : (wisselgeld > 0.01 ? '#DCFCE7' : '#F3F4F6') }}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Gedekt</span>
                      <span className="font-mono font-bold text-lg">{fmt(gedekt)}</span>
                    </div>
                    {tekort > 0.01 && (
                      <div className="flex items-center justify-between text-sm mt-1 text-amber-700 font-semibold">
                        <span className="flex items-center gap-1"><Warning size={14} /> Tekort</span>
                        <span className="font-mono text-lg" data-testid="kassa-tekort">{fmt(tekort)}</span>
                      </div>
                    )}
                    {wisselgeld > 0.01 && (
                      <div className="flex items-center justify-between text-sm mt-1 text-green-700 font-semibold">
                        <span>Wisselgeld (cash)</span>
                        <span className="font-mono text-lg" data-testid="kassa-wissel">{fmt(wisselgeld)}</span>
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2 mt-auto">
                    <button
                      className="flex-1 rounded border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 active:scale-[0.98]"
                      style={{ minHeight: 64 }}
                      onClick={() => setBetaalModus(false)}
                      data-testid="kassa-terug"
                    >← Terug</button>
                    <button
                      className="flex-[2] rounded bg-green-600 text-white font-bold text-xl hover:bg-green-700 active:scale-[0.98] disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      style={{ minHeight: 64 }}
                      onClick={afrekenen}
                      disabled={busy || tekort > 0.01}
                      data-testid="kassa-afrekenen"
                    >{busy ? 'Bezig…' : '✓ Afrekenen'}</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Onderaan: laatste tickets — horizontaal scrollend, compact */}
          <div className="mt-4 rounded" style={{ background: '#fff', border: '1px solid #E5E5E5' }}>
            <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between gap-3" style={{ background: '#F3F4F6' }}>
              <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Laatste tickets</span>
              <div className="flex items-center gap-3">
                <label className="text-[10px] text-gray-500 inline-flex items-center gap-1.5">
                  <span className="uppercase tracking-wider">Toon</span>
                  <select
                    className="text-[11px] font-mono bg-white border border-gray-300 rounded px-1.5 py-0.5 focus:border-gray-500 focus:outline-none"
                    value={tixLimit}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setTixLimit(v);
                      try { localStorage.setItem('kassa-tix-limit', String(v)); } catch (_) { /* quota */ }
                    }}
                    data-testid="kassa-tix-limit"
                  >
                    {[10, 20, 30, 40, 50].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <span className="text-[10px] text-gray-400">{verkopen.length} in totaal</span>
              </div>
            </div>
            <div className="p-2 overflow-x-auto">
              <div className="flex gap-2" style={{ minWidth: 'min-content' }}>
                {verkopen.slice(0, tixLimit).map(k => (
                  <div
                    key={k.id}
                    className={`shrink-0 w-32 p-2 rounded border bg-white hover:border-gray-400 ${
                      k.is_retour ? 'border-red-300 bg-red-50' : 'border-gray-200'
                    }`}
                    data-testid={`tix-${k.id}`}
                  >
                    <div className="text-[10px] font-mono text-gray-400 truncate">
                      {k.is_retour && <span className="text-red-600 font-bold">↩️ </span>}
                      {k.id}
                    </div>
                    <div className={`text-base font-bold font-mono ${k.is_retour ? 'text-red-600' : 'text-[#008A27]'}`}>
                      {fmt(k.totaal)}
                    </div>
                    <div className="text-[10px] text-gray-500 flex items-center justify-between mt-1">
                      <span>{k.tijd}</span>
                      <button
                        className="w-7 h-7 rounded hover:bg-gray-100 flex items-center justify-center text-gray-500"
                        onClick={() => printBon(k)}
                        title="Bon afdrukken"
                      ><Printer size={14} /></button>
                    </div>
                  </div>
                ))}
                {verkopen.length === 0 && <div className="text-center text-gray-400 py-6 text-xs w-full">Nog geen verkopen vandaag</div>}
              </div>
            </div>
          </div>
        </>
      )}

      {tab === 'z' && (
        <div style={{ background: '#fff', border: '1px solid #E5E5E5' }}>
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between" style={{ background: '#F3F4F6' }}>
            <div className="flex items-center gap-3">
              <Receipt size={18} />
              <span className="text-sm font-bold">Z-rapport</span>
              <input type="date" className="field-input text-xs" style={{ padding: '4px 6px' }} value={zDatum} onChange={e => setZDatum(e.target.value)} data-testid="z-datum" />
            </div>
            <div className="flex gap-2">
              <button className="btn btn-sm btn-ghost" onClick={() => window.print()} data-testid="z-print"><Printer size={12} /> Afdrukken</button>
              <button className="btn btn-sm btn-primary" onClick={dagNaarFactuur} disabled={busy || !zRapport?.aantal_tickets} data-testid="z-naar-factuur"><FileArrowDown size={12} /> Dag → factuur</button>
            </div>
          </div>
          <div className="p-4">
            {!zRapport && <div className="text-center text-gray-400 py-8">Laden…</div>}
            {zRapport && zRapport.aantal_tickets === 0 && <div className="text-center text-gray-400 py-8">Geen verkopen op {zDatum}.</div>}
            {zRapport && zRapport.aantal_tickets > 0 && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <KPI label="Aantal tickets" value={zRapport.aantal_tickets} testid="z-aantal" />
                  <KPI label="Omzet" value={fmt(zRapport.omzet_totaal)} accent="#002FA7" testid="z-omzet" />
                  {magWinst && zRapport.winst !== undefined && (
                    <>
                      <KPI label="Winst" value={fmt(zRapport.winst)} accent={zRapport.winst >= 0 ? '#008A27' : '#DC2626'} testid="z-winst" />
                      <KPI label="Marge" value={`${zRapport.marge_pct || 0}%`} accent="#7C3AED" testid="z-marge" />
                    </>
                  )}
                </div>

                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Per betaalwijze</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5">
                  {BETAALWIJZES.map(b => (
                    <div key={b.key} className="p-3 rounded border" style={{ background: (zRapport.per_betaalwijze?.[b.key] || 0) > 0 ? b.bg : '#fff', borderColor: '#E5E5E5' }}>
                      <div className="text-[10px] uppercase font-bold" style={{ color: b.kleur }}>{b.label}</div>
                      <div className="text-lg font-bold mt-1" style={{ color: b.kleur }}>{fmt(zRapport.per_betaalwijze?.[b.key] || 0)}</div>
                    </div>
                  ))}
                </div>

                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Tickets</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Nr</th><th>Tijd</th>
                      <th>Betaalwijzes</th>
                      <th className="right">Totaal</th>
                      {magWinst && <th className="right">Kostprijs</th>}
                      {magWinst && <th className="right">Winst</th>}
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(zRapport.tickets || []).map(t => {
                      const kost = parseFloat(t.kostprijs_totaal || 0);
                      const winst = (t.totaal || 0) / 1.21 - kost;
                      const hasFactuur = !!t.factuur_id;
                      const isRetour = !!t.is_retour || (t.id || '').startsWith('KR-');
                      return (
                        <tr
                          key={t.id}
                          className={isRetour ? 'bg-red-50' : ''}
                          data-testid={`kassa-ticket-row-${t.id}`}
                        >
                          <td className="mono">
                            <button
                              type="button"
                              className="doc-nr hover:underline cursor-pointer text-left disabled:no-underline disabled:cursor-not-allowed disabled:text-gray-400"
                              disabled={!magTicketOpenen}
                              onClick={async () => {
                                if (!magTicketOpenen) return;
                                try {
                                  const r = await API.get(`/kassa/${encodeURIComponent(t.id)}/details`);
                                  setTixDetail(r.data);
                                } catch (e) {
                                  alert('Ticket-details laden mislukt: ' + (e.response?.data?.detail || e.message));
                                }
                              }}
                              data-testid={`tix-open-${t.id}`}
                              title={magTicketOpenen ? 'Klik om ticket-details te zien' : 'Geen machtiging om ticket-details te openen'}
                            >
                              {isRetour && <span className="text-red-600 mr-1">↩️</span>}
                              {t.id}
                            </button>
                          </td>
                          <td>{t.tijd}</td>
                          <td className="text-xs">{(t.betaalwijzes || []).map(b => `${b.type} € ${b.bedrag.toFixed(2)}`).join(' · ') || t.betaalwijze}</td>
                          <td className={`right font-bold ${isRetour ? 'text-red-600' : 'text-[#008A27]'}`}>{fmt(t.totaal)}</td>
                          {magWinst && <td className="right font-mono text-gray-600">{fmt(kost)}</td>}
                          {magWinst && <td className="right font-bold" style={{ color: winst >= 0 ? '#008A27' : '#DC2626' }}>{fmt(winst)}</td>}
                          <td>{hasFactuur ? (
                            <span
                              className="doc-nr text-[10px] cursor-pointer hover:underline"
                              onClick={() => navigate(`/documenten/${t.factuur_id}/bewerk`)}
                              title="Open gekoppelde factuur"
                            >{t.factuur_id}</span>
                          ) : <span className="text-[10px] text-gray-400">—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'maand' && (
        <div style={{ background: '#fff', border: '1px solid #E5E5E5' }}>
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between" style={{ background: '#F3F4F6' }}>
            <div className="flex items-center gap-3">
              <CalendarBlank size={18} />
              <span className="text-sm font-bold">Maandoverzicht</span>
              <input type="month" className="field-input text-xs" style={{ padding: '4px 6px' }} value={maand} onChange={e => setMaand(e.target.value)} data-testid="maand-input" />
            </div>
          </div>
          <div className="p-4">
            {!maandData && <div className="text-center text-gray-400 py-8">Laden…</div>}
            {maandData && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                  <KPI label="Tickets in maand" value={maandData.aantal_tickets} testid="maand-aantal" />
                  <KPI label="Omzet maand" value={fmt(maandData.omzet_totaal)} accent="#002FA7" testid="maand-omzet" />
                  {magWinst && maandData.winst_totaal !== undefined && (
                    <KPI label="Winst maand" value={fmt(maandData.winst_totaal)} accent="#008A27" testid="maand-winst" />
                  )}
                </div>

                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Dag</th>
                      <th className="right">Tickets</th>
                      <th className="right">Contant</th>
                      <th className="right">Bancontact</th>
                      <th className="right">Visa</th>
                      <th className="right">Maestro</th>
                      <th className="right">Omzet</th>
                      {magWinst && <th className="right">Winst</th>}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(maandData.dagen || []).map(d => (
                      <tr
                        key={d.datum}
                        className="cursor-pointer hover:bg-[#F0F4FF] transition-colors"
                        onClick={() => { setZDatum(d.datum); setTab('z'); }}
                        data-testid={`kassa-dag-row-${d.datum}`}
                        title={`Open Z-rapport voor ${d.datum}`}
                      >
                        <td className="mono">{d.datum}</td>
                        <td className="right">{d.aantal}</td>
                        <td className="right text-gray-600">{fmt(d.per_bw?.contant || 0)}</td>
                        <td className="right text-gray-600">{fmt(d.per_bw?.bancontact || 0)}</td>
                        <td className="right text-gray-600">{fmt(d.per_bw?.visa || 0)}</td>
                        <td className="right text-gray-600">{fmt(d.per_bw?.maestro || 0)}</td>
                        <td className="right font-bold text-[#008A27]">{fmt(d.omzet)}</td>
                        {magWinst && <td className="right font-bold" style={{ color: (d.winst || 0) >= 0 ? '#008A27' : '#DC2626' }}>{fmt(d.winst || 0)}</td>}
                        <td onClick={e => e.stopPropagation()}>
                          <button className="text-[10px] text-[#002FA7] hover:underline" onClick={() => { setZDatum(d.datum); setTab('z'); }}>→ Z-rap</button>
                        </td>
                      </tr>
                    ))}
                    {(maandData.dagen || []).length === 0 && <tr><td colSpan="9" className="text-center text-gray-400 py-4 text-xs">Geen tickets deze maand</td></tr>}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}

      {/* Variant-picker modal — opent wanneer een artikel met varianten geklikt wordt */}
      <VariantPickerModal
        artikel={variantPicker?.artikel}
        open={!!variantPicker}
        onClose={() => setVariantPicker(null)}
        onSelect={(variant) => addArtikel(variantPicker.artikel, variant)}
        prijsGroep={prijsGroep}
      />

      {/* Sendcloud verzendlabel — voor telefonische bestelling versturen */}
      {verzendOpen && (() => {
        const t = verzendOpen;
        const k = klanten.find(x => x.id === t.klant_id) || {};
        const fullAdres = (k.lever_adres || k.adres || '').trim();
        const m = fullAdres.match(/^(.*?)\s+(\d+\w*)$/);
        const street = m ? m[1] : fullAdres;
        const house_number = m ? m[2] : '';
        const totaalGewicht = (t.lijnen || []).reduce((acc, l) => {
          const a = parseFloat(l.aantal) || 0;
          const g = parseFloat(l.gewicht_kg) || 0;
          return acc + Math.abs(a) * g;
        }, 0);
        return (
          <VerzendlabelModal
            open={true}
            onClose={() => setVerzendOpen(null)}
            bronType="kassa"
            bronId={t.id}
            initial={{
              name: t.klant_naam || k.bedrijf || `${k.voorn || ''} ${k.achtern || ''}`.trim() || k.naam || '',
              company: k.bedrijf || '',
              street,
              house_number,
              postal_code: k.lever_postcode || k.postcode || '',
              city: k.lever_gemeente || k.gemeente || '',
              country: (k.lever_land || k.land || 'BE').toUpperCase(),
              email: k.email || '',
              telephone: k.tel || k.gsm || '',
              weight: totaalGewicht > 0 ? totaalGewicht : 1.0,
            }}
          />
        );
      })()}

      {/* Ticket-detail modal — open vanuit Z-rapport */}
      {tixDetail && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => e.target === e.currentTarget && setTixDetail(null)}
          data-testid="tix-detail-modal"
        >
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div
              className={`px-5 py-4 border-b-2 flex items-center justify-between gap-3 ${
                tixDetail.is_retour ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-2xl">{tixDetail.is_retour ? '↩️' : '🧾'}</span>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-gray-600">
                    {tixDetail.is_retour ? 'Retour-ticket' : 'Verkoop-ticket'}
                  </div>
                  <div className="text-base font-mono font-bold text-gray-900">{tixDetail.id}</div>
                  <div className="text-[11px] text-gray-500">
                    {tixDetail.datum} · {tixDetail.tijd}
                    {tixDetail.klant_naam ? <> · klant: <b>{tixDetail.klant_naam}</b></> : <> · klant: <i>Kassaverkoop</i></>}
                    {tixDetail.verkoper_naam ? <> · verkoper: <b>{tixDetail.verkoper_naam}</b></> : ''}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setTixDetail(null)}
                className="text-gray-400 hover:text-gray-700 p-1 rounded hover:bg-white"
                title="Sluit"
                data-testid="tix-detail-sluit"
              ><X size={18} /></button>
            </div>

            <div className="p-5 overflow-y-auto">
              <h4 className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">Verkochte artikels</h4>
              <table className="w-full text-xs mb-4">
                <thead className="bg-gray-50">
                  <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                    <th className="text-left p-2">Omschrijving</th>
                    <th className="text-right p-2 w-16">Aantal</th>
                    <th className="text-right p-2 w-20">Prijs</th>
                    <th className="text-right p-2 w-24">Totaal</th>
                    {magTicketVerwijderen && <th className="text-center p-2 w-10"></th>}
                  </tr>
                </thead>
                <tbody>
                  {(tixDetail.lijnen || []).map((l, i) => {
                    const aant = parseFloat(l.aantal) || 1;
                    const prijs = parseFloat(l.prijs) || 0;
                    return (
                      <tr key={i} className="border-b border-gray-100" data-testid={`tix-detail-rij-${i}`}>
                        <td className="p-2">
                          <div className="font-medium">{l.omschr}</div>
                          {l.art_id && <div className="text-[9px] text-gray-400 font-mono">art: {l.art_id}</div>}
                          {l.variant_label && <div className="text-[9px] text-blue-700 font-mono">🎨 {l.variant_label}</div>}
                        </td>
                        <td className={`p-2 text-right font-mono ${aant < 0 ? 'text-red-600 font-bold' : ''}`}>{aant}</td>
                        <td className="p-2 text-right font-mono">{fmt(prijs)}</td>
                        <td className={`p-2 text-right font-mono font-bold ${aant * prijs < 0 ? 'text-red-600' : ''}`}>{fmt(aant * prijs)}</td>
                        {magTicketVerwijderen && (
                          <td className="p-2 text-center">
                            <button
                              type="button"
                              onClick={async () => {
                                const heeftArt = !!l.art_id;
                                const stap1 = window.confirm(
                                  `Verwijder deze regel uit het ticket?\n\n` +
                                  `${l.omschr}\nAantal: ${aant}  ·  Bedrag: ${fmt(aant * prijs)}\n\n` +
                                  `Het ticket-totaal en de betaalwijzes worden automatisch herrekend.\n\n` +
                                  `Klik OK om door te gaan, Annuleren om te stoppen.`
                                );
                                if (!stap1) return;
                                // Stap 2: enkel relevant als artikel een art_id heeft (anders geen voorraadeffect)
                                let voorraadTerug = false;
                                if (heeftArt) {
                                  voorraadTerug = window.confirm(
                                    `Voorraad terugboeken?\n\n` +
                                    `Klik OK = JA, voorraad +${aant} (artikel is nog verkoopbaar).\n` +
                                    `Klik Annuleren = NEE, voorraad blijft ongewijzigd (artikel beschadigd / weg).\n\n` +
                                    `STANDAARDADVIES: kies "Annuleren" als het artikel niet meer verkoopbaar is.`
                                  );
                                }
                                try {
                                  const r = await API.delete(
                                    `/kassa/${encodeURIComponent(tixDetail.id)}/lijn/${i}`,
                                    { params: { voorraad_terug: voorraadTerug } }
                                  );
                                  if (r.data?.ticket_verwijderd) {
                                    setTixDetail(null);
                                  } else {
                                    const f = await API.get(`/kassa/${encodeURIComponent(tixDetail.id)}/details`);
                                    setTixDetail(f.data);
                                  }
                                  load();
                                  if (tab === 'z') {
                                    API.get('/kassa/z-rapport', { params: { datum: zDatum } }).then(rr => setZRapport(rr.data)).catch(() => {});
                                  }
                                } catch (e) {
                                  alert('Regel verwijderen mislukt: ' + (e.response?.data?.detail || e.message));
                                }
                              }}
                              className="w-7 h-7 rounded text-red-500 hover:bg-red-50 hover:text-red-700 inline-flex items-center justify-center"
                              title="Regel verwijderen (admin) — keuze: voorraad wel/niet terug"
                              data-testid={`tix-detail-del-rij-${i}`}
                            ><X size={13} weight="bold" /></button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <h4 className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">Betaalwijzes</h4>
                  <div className="space-y-1">
                    {(tixDetail.betaalwijzes || []).map((b, i) => (
                      <div key={i} className="flex justify-between text-xs border-b border-gray-100 pb-1">
                        <span className="capitalize">{b.type}</span>
                        <span className="font-mono font-bold">{fmt(b.bedrag)}</span>
                      </div>
                    ))}
                    {(!tixDetail.betaalwijzes || tixDetail.betaalwijzes.length === 0) && (
                      <div className="text-xs text-gray-400 italic">Geen betaalwijzes geregistreerd</div>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">Totalen</h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between border-b border-gray-100 pb-1">
                      <span className="text-gray-600">Totaal</span>
                      <span className={`font-mono font-bold ${tixDetail.is_retour ? 'text-red-600' : 'text-green-700'}`}>{fmt(tixDetail.totaal)}</span>
                    </div>
                    {magWinst && tixDetail.kostprijs_totaal !== undefined && (
                      <>
                        <div className="flex justify-between border-b border-gray-100 pb-1">
                          <span className="text-gray-600">Kostprijs</span>
                          <span className="font-mono">{fmt(tixDetail.kostprijs_totaal)}</span>
                        </div>
                        <div className="flex justify-between border-b border-gray-100 pb-1">
                          <span className="text-gray-600">Winst (excl. BTW)</span>
                          <span className="font-mono font-bold">{fmt((tixDetail.totaal || 0) / 1.21 - (tixDetail.kostprijs_totaal || 0))}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {tixDetail.factuur_id && (
                <div className="mb-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-xs flex items-center justify-between">
                  <span className="text-blue-900">Gekoppeld aan factuur <b>{tixDetail.factuur_id}</b></span>
                  <button
                    className="text-[#002FA7] hover:underline font-bold text-[11px]"
                    onClick={() => navigate(`/documenten/${tixDetail.factuur_id}/bewerk`)}
                  >Open factuur →</button>
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-5 py-3 border-t border-gray-200 flex items-center justify-between gap-2">
              <div className="flex gap-2">
                <button
                  onClick={() => printBon(tixDetail)}
                  className="btn btn-sm btn-outline inline-flex items-center gap-1.5"
                  data-testid="tix-detail-print"
                >
                  <Printer size={12} /> Bon afdrukken
                </button>
                <button
                  onClick={() => setVerzendOpen(tixDetail)}
                  className="btn btn-sm bg-[#FF6B0D] text-white hover:bg-[#E55A00] inline-flex items-center gap-1.5"
                  data-testid="tix-detail-verzendlabel"
                  title="Telefonische bestelling versturen — maak verzendlabel via Sendcloud"
                >
                  📦 Verzendlabel
                </button>
              </div>
              <div className="flex gap-2">
                {magTicketVerwijderen && (
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Ticket ${tixDetail.id} verwijderen?\n\nDe voorraad wordt teruggezet naar de stand voor deze verkoop. Deze actie is niet ongedaan te maken.`)) return;
                      try {
                        await API.delete(`/kassa/${encodeURIComponent(tixDetail.id)}`);
                        setTixDetail(null);
                        load();
                        // Refresh Z-rapport
                        if (tab === 'z') {
                          API.get('/kassa/z-rapport', { params: { datum: zDatum } }).then(r => setZRapport(r.data)).catch(() => {});
                        }
                      } catch (e) {
                        alert('Verwijderen mislukt: ' + (e.response?.data?.detail || e.message));
                      }
                    }}
                    className="btn btn-sm bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-1.5"
                    data-testid="tix-detail-verwijder"
                    title="Admin: ticket verwijderen + voorraad terugzetten"
                  >
                    🗑️ Verwijderen (admin)
                  </button>
                )}
                <button
                  onClick={() => setTixDetail(null)}
                  className="btn btn-sm btn-ghost"
                  data-testid="tix-detail-close-btn"
                >Sluiten</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn({ actief, onClick, icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded transition ${actief ? 'bg-white text-[#002FA7] shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
    >{icon}{label}</button>
  );
}

function KPI({ label, value, accent = '#111', testid }) {
  return (
    <div className="kpi-card !p-3" data-testid={testid}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value !text-xl" style={{ color: accent }}>{value}</div>
    </div>
  );
}
